import React, { Component } from 'react';
import { Alert, AlertDescription } from "@/components/ui/Alert";
import {  AlertCircle, RefreshCcw  } from './icons/LucideIcons';
import { Button } from '@/components/ui/Button';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      isAndroidTablet: false,
      retryCount: 0,
      maxRetries: 3
    };
  }

  componentDidMount() {
    // Detect if device is Android tablet
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = /android/.test(userAgent);
    const isTablet = window.innerWidth >= 600 && window.innerHeight >= 600;
    this.setState({ isAndroidTablet: isAndroid && isTablet });
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log the error to console
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    
    // If we're on an Android tablet, try to recover automatically
    if (this.state.isAndroidTablet && this.state.retryCount < this.state.maxRetries) {
      console.log(`Android tablet detected. Attempting automatic recovery (${this.state.retryCount + 1}/${this.state.maxRetries})`);
      setTimeout(() => {
        this.setState(prevState => ({
          hasError: false,
          error: null,
          errorInfo: null,
          retryCount: prevState.retryCount + 1
        }));
      }, 1000); // Wait a second before retrying
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReportError = () => {
    // Implement error reporting logic here
    alert("Error was reported. We'll look into it. Thank you!");
  };

  render() {
    if (this.state.hasError) {
      // Special handling for Android tablets
      if (this.state.isAndroidTablet) {
        return (
          <div className="p-4 rounded-lg bg-white shadow-md my-2">
            <div className="flex flex-col items-center text-center p-4">
              <AlertCircle className="h-12 w-12 text-amber-500 mb-4" />
              <h3 className="text-lg font-medium text-amber-700 mb-2">
                Компонент временно недоступен
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Обнаружена проблема с этой частью приложения на вашем устройстве Android.
              </p>
              
              <Button 
                onClick={this.handleRetry}
                className="flex items-center bg-brand-500 text-white hover:bg-brand-600 px-4 py-2 rounded"
              >
                <RefreshCcw className="h-4 w-4 mr-2" /> Попробовать снова
              </Button>
              
              {/* Memory usage tips for Android */}
              <div className="mt-4 text-xs text-left bg-brand-50 p-3 rounded">
                <p className="font-medium text-brand-700 mb-1">Совет:</p>
                <ul className="list-disc pl-4 text-brand-700 space-y-1">
                  <li>Закройте другие приложения для освобождения памяти</li>
                  <li>Обновите страницу, если проблема повторяется</li>
                  <li>Используйте Wi-Fi для более стабильной работы</li>
                </ul>
              </div>
            </div>
          </div>
        );
      }
      
      // Default error UI for other devices
      return this.props.fallback || (
        <Alert variant="destructive">
          <AlertCircle className="h-5 w-5 mr-2" />
          <AlertDescription>
            Произошла ошибка. Пожалуйста, обновите страницу.
          </AlertDescription>
        </Alert>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;